import { useState, useEffect, useCallback } from 'react';
import { MessageCircle, X, Search, Maximize2 } from 'lucide-react';
import ChatWindow from './ChatWindow';

import { SECTION_COLOR } from './komunikace/komunikace';

// Avatar bez fotky — neutrálně, ne v barvě sekce (ta patří odznakům a akcím)
const AVATAR_COLOR = 'rgba(255,255,255,0.10)';

function formatCasMini(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (isToday) return d.toLocaleTimeString('cs', { hour: '2-digit', minute: '2-digit' });
    return `${d.getDate()}.${d.getMonth() + 1}.`;
}

export default function MiniChat({ user, onNavigate, isDark, currentPage }) {
    const [open, setOpen] = useState(false);
    const [konverzace, setKonverzace] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [unread, setUnread] = useState(0);
    const [search, setSearch] = useState('');
    const [onlineIds, setOnlineIds] = useState(new Set());

    // Synchronizace aktivní konverzace pro toast potlačení
    useEffect(() => {
        window.__aktivniKonverzaceId = (open && activeId) ? activeId : null;
        return () => { window.__aktivniKonverzaceId = null; };
    }, [open, activeId]);

    // Fetch konverzace
    const fetchKonverzace = useCallback(() => {
        if (currentPage === 'zpravy') return;
        fetch('/api/chat/konverzace').then(r => r.ok ? r.json() : []).then(data => {
            setKonverzace(data);
            const total = data.reduce((sum, k) => sum + (k.neprectenych || 0), 0);
            setUnread(total);
        }).catch(() => {});
    }, [currentPage]);

    // Seznam se tahá, jen když je okno otevřené — dřív každých 15 s na každé
    // stránce i se zavřeným MiniChatem. Počet nepřečtených nese app.jsx
    // (událost `chat-badge-update`).
    useEffect(() => {
        if (!open) return;
        fetchKonverzace();
        const i = setInterval(fetchKonverzace, 15000);
        return () => clearInterval(i);
    }, [open, fetchKonverzace]);
    useEffect(() => {
        const h = () => { if (open) fetchKonverzace(); };
        window.addEventListener('app-resumed', h);
        return () => window.removeEventListener('app-resumed', h);
    }, [open, fetchKonverzace]);

    // Online status
    useEffect(() => {
        if (currentPage === 'zpravy' || !open) return;
        const fetchOnline = () => fetch('/api/chat/online').then(r => r.ok ? r.json() : []).then(ids => setOnlineIds(new Set(ids))).catch(() => {});
        fetchOnline();
        const i = setInterval(fetchOnline, 30000);
        return () => clearInterval(i);
    }, [currentPage, open]);

    // Badge synchronizace z app.jsx
    useEffect(() => {
        const handler = (e) => setUnread(e.detail?.count ?? 0);
        window.addEventListener('chat-badge-update', handler);
        return () => window.removeEventListener('chat-badge-update', handler);
    }, []);

    // Toggle z Sidebar
    useEffect(() => {
        const handler = () => {
            setOpen(prev => {
                if (!prev) fetchKonverzace();
                return !prev;
            });
        };
        window.addEventListener('minichat-toggle', handler);
        return () => window.removeEventListener('minichat-toggle', handler);
    }, [fetchKonverzace]);

    // Skrýt na stránce Zprávy
    if (currentPage === 'zpravy') return null;

    const filtered = konverzace.filter(k =>
        !search || k.nazev?.toLowerCase().includes(search.toLowerCase())
    );

    // Plocha okna: neutrální `--surface-raised` (jako karty) se stínem modalu.
    // Dřív si okno míchalo vlastní modrošedé rgba(15,15,25) — brandbook modrou
    // na plochách zakazuje a vedle zbytku aplikace okno vypadalo jako cizí.
    const borderClr = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const zavrit = () => { setOpen(false); setActiveId(null); };
    const naCelouStranku = () => { const kid = activeId; setOpen(false); setActiveId(null); onNavigate('zpravy', { konverzaceId: kid }); };
    const ikonaOkna = (Icon, popis, onClick, size = 16) => (
        <button type="button" onClick={onClick} aria-label={popis} title={popis}
            className="flex items-center justify-center rounded-lg shrink-0 transition-colors"
            style={{ width: 30, height: 30, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
            <Icon size={size} />
        </button>
    );
    const akceOkna = (
        <div className="flex items-center shrink-0" style={{ gap: 2 }}>
            {ikonaOkna(Maximize2, 'Otevřít na celou stránku', naCelouStranku, 15)}
            {ikonaOkna(X, 'Zavřít', zavrit, 17)}
        </div>
    );

    return (
        <>
            {/* ─── Mini chat panel ─── */}
            {open && (
                <div className="fixed z-50 flex flex-col overflow-hidden"
                    style={{
                        bottom: 20, right: 20, width: 380, height: 'min(560px, calc(100vh - 40px))',
                        borderRadius: 16,
                        background: 'var(--surface-raised)',
                        border: `1px solid ${borderClr}`,
                        boxShadow: 'var(--modal-shadow)',
                    }}>
                    {/* Hlavička jen u seznamu — otevřená konverzace má vlastní
                        jednořádkovou (ChatWindow compact) s akcemi okna. Dvě
                        hlavičky nad sebou braly okno o šířce 380 px skoro o čtvrtinu. */}
                    {!activeId && (
                        <div className="flex items-center gap-2 shrink-0" style={{ padding: '10px 10px 10px 14px', borderBottom: `1px solid ${borderClr}` }}>
                            <MessageCircle size={17} style={{ color: SECTION_COLOR }} />
                            <span className="flex-1 font-semibold" style={{ color: 'var(--text-primary)', fontSize: 14 }}>
                                Zprávy
                                {unread > 0 && <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>· {unread > 99 ? '99+' : unread} nepřečtených</span>}
                            </span>
                            {akceOkna}
                        </div>
                    )}

                    {/* Obsah */}
                    <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
                        {!activeId ? (
                            /* ── Seznam konverzací ── */
                            <>
                                <div className="px-3 pt-3 pb-2 shrink-0">
                                    <div className="relative">
                                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                                        <input value={search} onChange={e => setSearch(e.target.value)}
                                            placeholder="Hledat konverzaci..."
                                            className="w-full text-xs outline-none pl-8 pr-3 py-1.5 rounded-lg"
                                            style={{
                                                height: 34, fontSize: 13,
                                                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                                color: 'var(--text-primary)',
                                            }} />
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
                                    {filtered.length === 0 && (
                                        <div className="text-center py-8 text-xs" style={{ color: 'var(--text-muted)' }}>Žádné konverzace</div>
                                    )}
                                    {filtered.map(k => {
                                        let isOnlineItem = false;
                                        let avatarEl;

                                        if (k.typ === 'soukromy') {
                                            const druhy = k.ucastnici?.find(u => u.id !== user?.id);
                                            isOnlineItem = onlineIds.has(druhy?.id);
                                            const url = druhy?.avatar_url;
                                            const ini = druhy?.jmeno
                                                ? druhy.jmeno.split(' ').filter(Boolean).map(w => w[0]?.toUpperCase()).slice(0, 2).join('')
                                                : '?';
                                            avatarEl = url
                                                ? <img src={url} alt={druhy?.jmeno} className="w-10 h-10 rounded-full object-cover" />
                                                : <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs"
                                                    style={{ background: AVATAR_COLOR }}>{ini}</div>;
                                        } else {
                                            isOnlineItem = k.ucastnici?.some(u => u.id !== user?.id && onlineIds.has(u.id));
                                            if (k.avatar_url) {
                                                avatarEl = <img src={k.avatar_url} alt={k.nazev} className="w-10 h-10 rounded-full object-cover" />;
                                            } else {
                                                avatarEl = <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs"
                                                    style={{ background: AVATAR_COLOR }}>
                                                    {k.ikona || (k.typ === 'sdeleni' ? '📢' : k.nazev?.[0]?.toUpperCase() || '?')}
                                                </div>;
                                            }
                                        }

                                        const posledniAutorJsemJa = k.posledni_zprava?.odesilatel_id === user?.id;
                                        const posledniText = k.posledni_zprava
                                            ? `${posledniAutorJsemJa ? 'Vy' : k.posledni_zprava.autor}: ${k.posledni_zprava.obsah}`
                                            : 'Žádné zprávy';

                                        return (
                                            <button key={k.id} onClick={() => setActiveId(k.id)}
                                                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors"
                                                style={{ background: 'transparent' }}
                                                onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                <div className="relative flex-shrink-0">
                                                    {avatarEl}
                                                    {isOnlineItem && (
                                                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
                                                            style={{ background: '#22C55E', borderColor: 'var(--surface-raised)' }} />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-semibold truncate" style={{ color: 'var(--text-primary)', fontSize: 13.5 }}>{k.nazev}</span>
                                                        {k.posledni_zprava && (
                                                            <span className="flex-shrink-0 ml-1" style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                                                {formatCasMini(k.posledni_zprava.cas)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center justify-between mt-0.5">
                                                        <span className="truncate" style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                                            {posledniText}
                                                        </span>
                                                        {k.neprectenych > 0 && (
                                                            <span className="flex-shrink-0 ml-1 text-[10px] font-bold text-white rounded-full px-1.5 py-0.5"
                                                                style={{ background: SECTION_COLOR, minWidth: '16px', textAlign: 'center' }}>
                                                                {k.neprectenych > 9 ? '9+' : k.neprectenych}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            /* ── Chat okno ── */
                            <ChatWindow konverzaceId={activeId} user={user} isDark={isDark} compact akceOkna={akceOkna}
                                isOnline={(() => {
                                    const konv = konverzace.find(k => k.id === activeId);
                                    const druhy = konv?.ucastnici?.find(u => u.id !== user?.id);
                                    return druhy ? onlineIds.has(druhy.id) : false;
                                })()}
                                onBack={() => setActiveId(null)}
                                onKonverzaceZmenena={fetchKonverzace} />
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
